import * as React from "react";

interface ResultFile {
    fileName: string;
    base64Content: string;
    encoding?: string;
}

function decodeFile(file: ResultFile): string | null {
    try {
        if (file.encoding === "ISO-8859-1") return atob(file.base64Content);
        return decodeURIComponent(escape(atob(file.base64Content)));
    } catch {
        try { return atob(file.base64Content); } catch { return null; }
    }
}

type FileFormat = "soloplan" | "sauer" | "komalog" | "translogica" | "winsped" | "json" | "xml" | "pdf" | "csv" | "txt" | "other";

function detectFileFormat(file: ResultFile): FileFormat {
    const name = (file.fileName ?? "").toLowerCase();
    if (name.endsWith(".pdf")) return "pdf";
    if (name.endsWith(".csv")) return "csv";
    if (name.endsWith(".txt")) {
        const content = decodeFile(file);
        if (content && content.trim().startsWith("START|")) return "winsped";
        return "txt";
    }
    if (name.endsWith(".xml")) {
        const content = decodeFile(file);
        if (content) {
            if (content.includes("<TranslogicaOrder")) return "translogica";
            if (content.includes("<transportations"))  return "komalog";
        }
        return "xml";
    }
    if (name.endsWith(".json")) {
        const content = decodeFile(file);
        if (!content) return "json";
        try {
            const parsed = JSON.parse(content);
            if (Array.isArray(parsed.order) && parsed.header?.exportItemReference !== undefined) return "soloplan";
            if (parsed.stringBody) {
                try {
                    const inner = JSON.parse(parsed.stringBody);
                    if (inner.BEL_NAME !== undefined || inner.ENTL_NAME !== undefined || inner.LADNRK !== undefined) return "sauer";
                } catch { /* ignore */ }
            }
            if (parsed.BEL_NAME !== undefined || parsed.ENTL_NAME !== undefined || parsed.LADNRK !== undefined) return "sauer";
        } catch { /* ignore */ }
        return "json";
    }
    return "other";
}

const formatColors: Record<FileFormat, { bg: string; text: string; label: string }> = {
    soloplan: { bg: "#1a6eb5", text: "#fff", label: "Soloplan" },
    sauer:    { bg: "#2e7d32", text: "#fff", label: "Sauer" },
    komalog:     { bg: "#b84500", text: "#fff", label: "Komalog" },
    translogica: { bg: "#e8a000", text: "#fff", label: "Translogica" },
    winsped:     { bg: "#006060", text: "#fff", label: "WinSped" },
    json:     { bg: "#6b5b00", text: "#fff", label: "JSON" },
    xml:      { bg: "#7b1fa2", text: "#fff", label: "XML" },
    pdf:      { bg: "#c62828", text: "#fff", label: "PDF" },
    csv:      { bg: "#00695c", text: "#fff", label: "CSV" },
    txt:      { bg: "#546e7a", text: "#fff", label: "TXT" },
    other:    { bg: "#757575", text: "#fff", label: "File" },
};

export interface FileSelectorProps {
    converterOutput: string;
    selectedIndex: number;
    onSelect: (index: number) => void;
}

export function FileSelector({ converterOutput, selectedIndex, onSelect }: FileSelectorProps) {
    const files: ResultFile[] = React.useMemo(() => {
        if (!converterOutput) return [];
        try {
            const parsed = JSON.parse(converterOutput);
            return Array.isArray(parsed.resultFiles) ? parsed.resultFiles : [];
        } catch { return []; }
    }, [converterOutput]);

    const formats = React.useMemo(
        () => files.map(f => detectFileFormat(f)),
        [files]
    );

    if (files.length <= 1) return null;

    return (
        <div style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 4,
            padding: "4px 6px",
            background: "#f4f4f4",
            borderBottom: "1px solid #ddd",
            alignItems: "center",
        }}>
            <span style={{ fontSize: 11, color: "#555", marginRight: 4, whiteSpace: "nowrap" }}>
                {files.length} Files:
            </span>
            {files.map((file, i) => {
                const fmt = formats[i];
                const colors = formatColors[fmt];
                const isSelected = i === selectedIndex;
                return (
                    <button
                        key={i}
                        onClick={() => onSelect(i)}
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            padding: "2px 8px 2px 4px",
                            border: isSelected ? "2px solid #333" : "1px solid #bbb",
                            borderRadius: 3,
                            background: isSelected ? "#fff" : "#fafafa",
                            cursor: "pointer",
                            fontSize: 11,
                            fontWeight: isSelected ? "bold" : "normal",
                            boxShadow: isSelected ? "0 1px 3px rgba(0,0,0,0.2)" : "none",
                            maxWidth: 220,
                            overflow: "hidden",
                        }}
                        title={file.fileName}
                    >
                        <span style={{
                            background: colors.bg,
                            color: colors.text,
                            fontSize: 9,
                            fontWeight: "bold",
                            padding: "1px 4px",
                            borderRadius: 2,
                            whiteSpace: "nowrap",
                            flexShrink: 0,
                        }}>
                            {colors.label}
                        </span>
                        <span style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            color: "#222",
                        }}>
                            {file.fileName}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}
